/**
 * @file Sidebar.tsx
 * @description Styled sidebar with consistent branding and modern animation.
 */

import { NavLink } from "react-router-dom";
import { logout } from "../lib/auth";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { User as UserIcon } from "lucide-react";
import { useFeatures, hasFeature } from "../lib/features";

const navItemBase =
  "block px-4 py-2 rounded-md transition-colors duration-150 font-medium";
const navItemActive = "bg-white text-black";
const navItemInactive = "hover:bg-white hover:text-black text-white";

export default function Sidebar() {
  const features = useFeatures();

  return (
    <motion.div
      initial={{ x: -30, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="w-64 p-6 flex flex-col justify-between bg-black min-h-screen"
    >
      {/* 🚀 Logo + Navigation */}
      <div>
        <div className="text-2xl font-bold tracking-tight mb-8">
          <span className="text-white">Infuse</span>
          <span className="text-emerald-400">ED</span>
        </div>

        <nav className="space-y-2">
          <NavLink
            to="/app"
            end
            className={({ isActive }) =>
              `${navItemBase} ${isActive ? navItemActive : navItemInactive}`
            }
          >
            Dashboard
          </NavLink>

          {/* Paid/entitled modules */}
          {hasFeature(features, "seat_smart") && (
            <NavLink
              to="/app/seek-and-destroy"
              className={({ isActive }) =>
                `${navItemBase} ${isActive ? navItemActive : navItemInactive}`
              }
            >
              SeatSmart
            </NavLink>
          )}

          {/* Uncomment when these routes exist */}
          {/*
          {hasFeature(features, "roster_ready") && (
            <NavLink
              to="/app/roster-ready"
              className={({ isActive }) =>
                `${navItemBase} ${isActive ? navItemActive : navItemInactive}`
              }
            >
              RosterReady
            </NavLink>
          )}

          {hasFeature(features, "field_guide_ai") && (
            <NavLink
              to="/app/field-guide"
              className={({ isActive }) =>
                `${navItemBase} ${isActive ? navItemActive : navItemInactive}`
              }
            >
              Field Guide AI
            </NavLink>
          )}
          */}

          <NavLink
            to="/app/help"
            className={({ isActive }) =>
              `${navItemBase} ${isActive ? navItemActive : navItemInactive}`
            }
          >
            Help
          </NavLink>
        </nav>
      </div>

      {/* 👤 Profile + 🔓 Logout */}
      <div className="space-y-3">
        <NavLink
          to="/app/settings/org-profile"
          className={({ isActive }) =>
            [
              "flex items-center gap-2 px-4 py-2 rounded-md border font-medium transition-colors",
              isActive
                ? "bg-white text-black border-white"
                : "text-white border-white/30 hover:bg-white hover:text-black",
            ].join(" ")
          }
        >
          <UserIcon className="h-4 w-4" />
          Profile
        </NavLink>

        <Button
          onClick={() => logout()}
          className="w-full border border-white/30 text-white hover:bg-white hover:text-black transition"
        >
          Sign Out
        </Button>
      </div>
    </motion.div>
  );
}
